import OverworldAction from './OverworldAction.js';
import GridMap from './GridMap.js';

import { getOSM } from '../OverworldAnimation.js';
import {
    UP,
    DOWN,
    LEFT,
    RIGHT,
    IDLE,
    MOVING,
    MOVE_N_STEPS,
    END_MOVING,
    PLAYER_SEEN,
    APPROACHED_PLAYER,
    APPROACHED_BY_TRAINER,
    MOVEMENT_PATTERN,
    INTERACTING,
} from '../constants.js';

export default class NPCStateMachine {
    serialize() {
        console.log(`Serializing ${this.constructor.name}`);
        return {
            currentState: this.currentState,
            movementSpeed: this.movementSpeed,
            context: this.context,
            // Don't serialize: actions, stateQueue (runtime), collisionMap, viewRangeMap (rebuilt)
            // npc reference will be restored when NPC creates the state machine
        };
    }

    static deserialize(data, npc) {
        const stateMachine = new NPCStateMachine({ npc });
        stateMachine.currentState = data.currentState;
        stateMachine.movementSpeed = data.movementSpeed;
        stateMachine.context = data.context || {};
        return stateMachine;
    }

    constructor({ npc }) {
        this.npc = npc;
        npc.stateMachine = this;
        this.movementSpeed = 1;
        this.currentState = IDLE;
        this.stateQueue = [];
        this.context = {};
        this.actions = [];
        this.collisionMap = new GridMap(16, 70 * 16, 40 * 16);
        this.viewRangeMap = new GridMap(16, 70 * 16, 40 * 16);
        this.executeMovement = false;
        this.movementTimer = setInterval(() => {
            this.executeMovement = true;
        }, 1000);
    }

    setUpActions() {
        switch (this.currentState) {
            case IDLE:
                this.actions = [
                    new OverworldAction({
                        action: () => {
                            if (this.npc.isInteracting) {
                                return true;
                            }
                            if (this.stateQueue.length > 0) {
                                return true;
                            }
                            return true;
                        },
                    }),
                    new OverworldAction({
                        action: () => {
                            return true;
                        },
                        duration: 0,
                    }),
                ];
                break;
            case MOVEMENT_PATTERN:
                this.executeMovement = false;
                this.actions.push(
                    new OverworldAction({
                        action: () => {
                            if (this.npc.isInteracting) {
                                return true;
                            }
                            switch (this.npc.movementPattern) {
                                case 'idle':
                                    return true;
                                case 'faceRandomDirection':
                                    this.npc.faceRandomDirection();
                                    return true;
                            }
                            return true;
                        },
                        duration: 0,
                    })
                );
                break;
            case INTERACTING:
                this.actions = [];
                break;
            case MOVE_N_STEPS:
                // Move NPC
                this.npc.sprite.moving = true;
                this.npc.sprite.hold = 8;
                this.actions.push(
                    new OverworldAction({
                        startAction: () => {
                            this.context.startPosition = {
                                x: this.npc.position.x,
                                y: this.npc.position.y,
                            };
                        },
                        action: () => {
                            if ([LEFT, RIGHT].includes(this.npc.direction)) {
                                if (this.npc.direction === RIGHT) {
                                    this.npc.position.x += this.npc.movementSpeed;
                                    return (
                                        this.npc.position.x ===
                                        this.context.startPosition.x + this.context.requiredSteps * 16
                                    );
                                }
                                if (this.npc.direction === LEFT) {
                                    this.npc.position.x -= this.npc.movementSpeed;
                                    return (
                                        this.npc.position.x ===
                                        this.context.startPosition.x - this.context.requiredSteps * 16
                                    );
                                }
                            } else if ([UP, DOWN].includes(this.npc.direction)) {
                                if (this.npc.direction === UP) {
                                    this.npc.position.y -= this.npc.movementSpeed;
                                    return (
                                        this.npc.position.y ===
                                        this.context.startPosition.y - this.context.requiredSteps * 16
                                    );
                                }
                                if (this.npc.direction === DOWN) {
                                    this.npc.position.y += this.npc.movementSpeed;
                                    return (
                                        this.npc.position.y ===
                                        this.context.startPosition.y + this.context.requiredSteps * 16
                                    );
                                }
                            }
                        },
                        endAction: () => {
                            this.context.requiredSteps = 0;
                        },
                    })
                );
                break;

            case MOVING:
                // Move NPC
                this.npc.sprite.moving = true;
                this.npc.sprite.hold = 8;
                this.actions = [
                    new OverworldAction({
                        startAction: () => {
                            this.resetCollisions();
                            this.updateCollisions();
                        },
                        action: () => {
                            if (!this.canMove()) {
                                console.log('NPC tried to move but there was a boundary');
                                return true;
                            }
                            // this.npc.sprite.moving = true
                            // this.npc.sprite.hold = 32
                            if (this.npc.direction === UP) {
                                this.npc.position.y -= this.npc.movementSpeed;
                            }
                            if (this.npc.direction === RIGHT) {
                                this.npc.position.x += this.npc.movementSpeed;
                            }
                            if (this.npc.direction === LEFT) {
                                this.npc.position.x -= this.npc.movementSpeed;
                            }
                            if (this.npc.direction === DOWN) {
                                this.npc.position.y += this.npc.movementSpeed;
                            }
                            return this.npc.alignedToGrid();
                        },
                        endAction: () => {
                            this.resetCollisions();
                            this.updateCollisions();
                        },
                    }),
                ];
                break;
            case END_MOVING:
                this.npc.sprite.moving = false;
                this.npc.sprite.frames.val = 0;
                break;
            case PLAYER_SEEN:
                // CALCULATE DISTANCE TO PLAYER
                this.actions = [];
                this.actions.push(
                    new OverworldAction({
                        startAction: () => {
                            console.log('Moving distance: ', this.npc.distanceToPlayer() - 1);
                            this.npc.showExclamationMark();
                        },
                        action: () => {
                            return true;
                        },
                        duration: 30,
                    })
                );
                this.actions.push(
                    new OverworldAction({
                        action: () => {
                            for (let i = 0; i < this.npc.distanceToPlayer() - 1; i++) {
                                // this.npc.sprite.moving = true
                                // this.npc.sprite.hold = 32
                                this.stateQueue.push(MOVING);
                            }
                            this.stateQueue.push(END_MOVING);
                            this.stateQueue.push(APPROACHED_PLAYER);
                            return true;
                        },
                    })
                );
                break;
            case APPROACHED_PLAYER:
                this.actions = [];
                this.actions.push(
                    new OverworldAction({
                        action: () => {
                            getOSM().stateQueue.push(APPROACHED_BY_TRAINER);
                            return true;
                        },
                    }),
                    new OverworldAction({
                        action: () => {
                            this.npc.sprite.moving = false;
                            return this.npc.isDefeated || getOSM().player.whitedOut();
                        },
                    })
                );
                break;
        }
        this.actions.forEach((action) => (action.stateMachine = this));

        this.updateCollisions();
    }

    resetCollisions() {
        this.npc.collisions = {
            up: false,
            right: false,
            down: false,
            left: false,
        };
    }

    updateCollisions() {
        this.npc.collisions.up = this.collisionMap.get(this.npc.position.x, this.npc.position.y - 16);
        this.npc.collisions.right = this.collisionMap.get(this.npc.position.x + 16, this.npc.position.y);
        this.npc.collisions.down = this.collisionMap.get(this.npc.position.x, this.npc.position.y + 16);
        this.npc.collisions.left = this.collisionMap.get(this.npc.position.x - 16, this.npc.position.y);
    }

    updateViewRange() {
        const viewRange = this.npc.viewRange;
        if (!viewRange) {
            return;
        }
        for (let i = 1; i < viewRange + 1; i++) {
            switch (this.npc.direction) {
                case UP:
                    this.viewRangeMap.set(this.npc.position.x, this.npc.position.y - 16 * i, true);
                    break;
                case RIGHT:
                    this.viewRangeMap.set(this.npc.position.x + 16 * i, this.npc.position.y, true);
                    break;
                case DOWN:
                    this.viewRangeMap.set(this.npc.position.x, this.npc.position.y + 16 * i, true);
                    break;
                case LEFT:
                    this.viewRangeMap.set(this.npc.position.x - 16 * i, this.npc.position.y, true);
                    break;
            }
        }
    }

    canMove() {
        return !this.npc.collisions[this.npc.direction];
    }

    transition() {
        // console.log(this.currentState);
        if (this.stateQueue.length > 0) {
            // debugger;
            this.currentState = this.stateQueue.shift();
            this.setUpActions();
            return;
        }
        switch (this.currentState) {
            case IDLE:
                this.currentState = IDLE;
                if (this.executeMovement) {
                    this.currentState = MOVEMENT_PATTERN;
                    break;
                }
                if (this.npc.isInteracting) {
                    this.currentState = INTERACTING;
                }
                break;
            case INTERACTING:
                if (!this.npc.isInteracting) {
                    this.currentState = IDLE;
                }
                break;
            case MOVING:
                this.currentState = END_MOVING;
                break;
            case END_MOVING:
                this.currentState = IDLE;
                break;
            case PLAYER_SEEN:
                break;
            case APPROACHED_PLAYER:
                this.currentState = IDLE;
                break;
            case MOVEMENT_PATTERN:
                this.currentState = IDLE;
                break;
        }
        this.setUpActions();
    }

    update() {
        this.collisionMap.clear();
        getOSM().GUI.boundaries.forEach((boundary) => {
            this.collisionMap.set(boundary.position.x, boundary.position.y, boundary);
        });
        getOSM().GUI.rightLedges.forEach((rightLedge) => {
            this.collisionMap.set(rightLedge.position.x, rightLedge.position.y, rightLedge);
        });
        getOSM().GUI.leftLedges.forEach((leftLedge) => {
            this.collisionMap.set(leftLedge.position.x, leftLedge.position.y, leftLedge);
        });
        getOSM().GUI.downLedges.forEach((downLedge) => {
            this.collisionMap.set(downLedge.position.x, downLedge.position.y, downLedge);
        });
        getOSM().GUI.npcs.forEach((npc) => {
            if (npc === this.npc) {
                return;
            }
            if (npc.position.x % 16 !== 0) {
                const npcXOffset = npc.position.x % 16;
                this.collisionMap.set(npc.position.x - npcXOffset, npc.position.y, true);
                this.collisionMap.set(npc.position.x - npcXOffset + 16, npc.position.y, true);
            }
            if (npc.position.y % 16 !== 0) {
                const npcYOffset = npc.position.y % 16;
                this.collisionMap.set(npc.position.x, npc.position.y - npcYOffset, true);
                this.collisionMap.set(npc.position.x, npc.position.y - npcYOffset + 16, true);
            }
            if (npc.position.x % 16 === 0 && npc.position.y % 16 === 0) {
                this.collisionMap.set(npc.position.x, npc.position.y, true);
            }
        });

        const playerX = getOSM().player.position.x;
        const playerXOffset = playerX % 16;
        const playerY = getOSM().player.position.y;
        const playerYOffset = playerY % 16;
        if (getOSM().player.position.x % 16 !== 0) {
            this.collisionMap.set(playerX - playerXOffset, playerY, true);
            this.collisionMap.set(playerX - playerXOffset + 16, playerY, true);
        }
        if (getOSM().player.position.y % 16 !== 0) {
            this.collisionMap.set(playerX, playerY - playerYOffset, true);
            this.collisionMap.set(playerX, playerY - playerYOffset + 16, true);
        }
        if (getOSM().player.position.x % 16 === 0 && getOSM().player.position.y % 16 === 0) {
            this.collisionMap.set(getOSM().player.position.x, getOSM().player.position.y, getOSM().player);
        }

        this.viewRangeMap.clear();
        this.updateCollisions();
        this.updateViewRange();
        if (this.currentAction) {
            // We are currently performing an action
            if (this.currentAction.ended) {
                // We're done with the action
                this.currentAction = this.actions.shift();
            } else {
                // We keep doing the action
                this.currentAction.execute();
            }
        } else {
            if (this.actions.length === 0) {
                // We have no more actions for this phase.
                // We move on to the next phase
                this.transition();
            } else {
                // We grab the next action from the queue
                this.currentAction = this.actions.shift();
            }
        }
    }
}
